import React from "react";
import { AlertTriangle } from "lucide-react";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { Slice, Fragment } from "prosemirror-model";
import { ySyncPluginKey } from "y-prosemirror";
import { createRoot } from "react-dom/client";
import { MAX_PASTE_CONTENT_CHARS } from "@/lib/upload-limits";

/**
 * Fired (on `window`) when a paste exceeds MAX_PASTE_CONTENT_CHARS. The inline
 * paste is cancelled; a React host (LargePasteImportModal, mounted globally in
 * providers.tsx) listens for this and shows a "too large — try import instead"
 * notice. The notice is informational only — the user imports via the normal
 * import flow, so no payload is passed with the event.
 */
export const LARGE_PASTE_EVENT = "editor-large-paste";

// Block types that are NEVER created from the editor itself — boards/pages/meetings
// are created only via dedicated flows and carry state OUTSIDE the document
// (datasource, view config, child blocks). Re-introducing one via paste or
// undo-restore yields a node with no backing data → render crash. So we strip them
// from pastes and make their deletion non-undoable (see below).
const NON_EDITOR_CREATABLE_TYPES = ["view_collection", "page", "meeting", "button"];

/** Recursively removes NON_EDITOR_CREATABLE_TYPES nodes from a pasted fragment. */
function stripNonEditorCreatableNodes(fragment: Fragment): Fragment {
  const kept: any[] = [];
  fragment.forEach((node: any) => {
    if (NON_EDITOR_CREATABLE_TYPES.includes(node.type?.name)) return; // drop it
    if (node.content && node.content.childCount > 0) {
      kept.push(node.copy(stripNonEditorCreatableNodes(node.content)));
    } else {
      kept.push(node);
    }
  });
  return Fragment.fromArray(kept);
}

// Helper function to delete board with optimistic updates
function deleteBoardWithOptimisticUpdate(blockId: string) {
  // Record the deletion so undo/redo can't bring this board's node back (its datasource
  // is being removed) — the appendTransaction guard strips any restored board in this set.
  deletedBoardIds.add(blockId);

  // Access global blocks context via window reference
  const globalBlocks = (window as any).__globalBlocks;

  const boardBlock = globalBlocks?.getBlock?.(blockId);
  if (!boardBlock) {
    return;
  }

  const parentId = boardBlock.parentId;
  const parentBlock = parentId ? globalBlocks?.getBlock?.(parentId) : null;
  // Optimistic update: drop the board from its parent's blockIds in the live context.
  if (parentBlock && parentBlock.blockIds) {
    const updatedBlockIds = parentBlock.blockIds.filter((id: string) => id !== blockId);
    globalBlocks?.updateBlock?.(parentId, { blockIds: updatedBlockIds });
  }

  // No direct API call. Now that the board node is properly removed from the doc, the
  // normal delete flow handles the backend: BlockTracker → BLOCK_DELETE → SyncWatcher →
  // /permanent-delete (offline-safe + retried). This helper only records the deletion
  // (for the undo guard, above) and does the optimistic context update.
}

// Singleton guard for the confirmation modal so repeated keypresses don't spawn multiples
let boardDeleteModalOpen = false;
let boardDeleteModalPromise: Promise<boolean> | null = null;
const CONFIRM_META = 'boardDeletionConfirmed';
// Set by UI that deletes a board while the editor is NOT focused (e.g. the
// drag-handle menu), which the focus check below would otherwise skip.
export const USER_BOARD_DELETE_META = 'boardDeletionUserInitiated';

// Board ids the user has DELETED this session. A deleted board's datasource is gone, so
// if undo/redo tries to restore its node we must strip it (it would crash). Gating the
// strip on this set means a board that was only CREATED (not deleted) is absent here, so
// redo-of-board-create still works.
const deletedBoardIds = new Set<string>();

const BOARD_DELETE_WARNING =
  "Their datasources and all of their pages will be deleted too.";

function getBoardDeleteMessage(count: number) {
  if (count > 1) {
    return `Are you sure you want to delete the selected ${count} boards?`;
  }
  return "Are you sure you want to delete this board?";
}

function getBoardDeleteWarning(count: number) {
  return count > 1
    ? BOARD_DELETE_WARNING
    : "Its datasource and all of its pages will be deleted too.";
}

function collectBoardsInRange(state: any, from: number, to: number) {
  const boards: Array<{ from: number; to: number; viewId: string | null }> = [];
  const seen = new Set<string>();

  state.doc.descendants((node: any, pos: number) => {
    if (node.type?.name !== "view_collection") {
      return true;
    }

    const nodeFrom = pos;
    const nodeTo = pos + node.nodeSize;
    const overlapsSelection = nodeFrom < to && nodeTo > from;
    if (!overlapsSelection) {
      return true;
    }

    const viewId = node.attrs?.blockId ?? null;
    const key = viewId ?? `${nodeFrom}:${nodeTo}`;
    if (seen.has(key)) {
      return true;
    }

    seen.add(key);
    boards.push({ from: nodeFrom, to: nodeTo, viewId });
    return true;
  });

  return boards;
}

function collectBoardsFromDoc(doc: any) {
  const list: Array<{ from: number; to: number; viewId: string | null }> = [];
  doc.descendants((node: any, pos: number) => {
    if (node.type?.name === "view_collection") {
      list.push({ from: pos, to: pos + node.nodeSize, viewId: node.attrs?.blockId ?? null });
    }
  });
  return list;
}

function getRemovedBoardsAfterDelete(state: any, from: number, to: number) {
  const beforeBoards = collectBoardsFromDoc(state.doc);
  const deletedDoc = state.tr.delete(from, to).doc;
  const afterBoards = collectBoardsFromDoc(deletedDoc);
  const afterViewIds = new Set(
    afterBoards.map((b) => b.viewId).filter((id): id is string => Boolean(id)),
  );
  // Only compare stable block ids — position-based keys break after delete().
  return beforeBoards.filter((b) => b.viewId && !afterViewIds.has(b.viewId));
}

// When a Backspace ("before") / Delete ("after") would land on a page block, LEAVE the
// page and jump the caret just past it — so continued line-by-line deletion keeps wiping
// the blocks BEYOND the page instead of removing the page itself. Returns true if it
// skipped a page (the keypress is then consumed). Covers a directly-selected page node
// and a page immediately adjacent to a collapsed caret.
function skipPageBlock(editor: any, dir: "before" | "after"): boolean {
  const { state } = editor;
  const { selection } = state;

  let range: { start: number; end: number } | null = null;
  if ((selection as any).node?.type?.name === "page") {
    range = { start: selection.from, end: selection.to };
  } else if (selection.empty) {
    const $from = (selection as any).$from;
    try {
      if (dir === "before" && $from.parentOffset === 0) {
        const boundary = $from.before(); // position just before the caret's block = page's end
        const node = (state.doc.resolve(boundary) as any).nodeBefore;
        if (node?.type?.name === "page") range = { start: boundary - node.nodeSize, end: boundary };
      } else if (dir === "after" && $from.parentOffset === $from.parent.content.size) {
        const boundary = $from.after(); // position just after the caret's block = page's start
        const node = (state.doc.resolve(boundary) as any).nodeAfter;
        if (node?.type?.name === "page") range = { start: boundary, end: boundary + node.nodeSize };
      }
    } catch {
      // position math at a document edge — treat as "no page", let default delete run
    }
  }

  if (!range) return false;

  // Move the caret just PAST the page — end of the block before it (Backspace) or start of
  // the block after it (Delete) — leaving the page intact so continued deletion wipes the
  // blocks beyond it. If the adjacent block isn't a textblock (e.g. another atom), we still
  // consume the keypress so the page is never removed.
  const targetPos = dir === "before" ? range.start - 1 : range.end + 1;
  const clamped = Math.max(0, Math.min(state.doc.content.size, targetPos));
  const $target = state.doc.resolve(clamped);
  if ($target.parent.isTextblock) {
    editor.view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, clamped)).scrollIntoView());
  }
  return true;
}

function deleteRangeWithConfirmMeta(editor: any, from: number, to: number) {
  const view = editor?.view;
  if (!view) return;
  const { state, dispatch } = view;
  let tr = state.tr.delete(from, to);
  tr = tr.setMeta(CONFIRM_META, true);
  // This delete is intentionally KEPT in undo history. It used to be addToHistory:false,
  // but that desynced the stack — Ctrl+Z then applied a stale step (RangeError / partial
  // "other blocks" restore). A deleted board is instead stripped on undo via the
  // deletedBoardIds guard in appendTransaction, so undo restores the text but not the board.
  dispatch(tr);
}

function showBoardDeleteModal(message: string, warning?: string): Promise<boolean> {
  if (typeof document === "undefined") return Promise.resolve(false);
  if (boardDeleteModalOpen && boardDeleteModalPromise) {
    return boardDeleteModalPromise;
  }
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.inset = "0";
  container.style.zIndex = "999999";
  document.body.appendChild(container);
  boardDeleteModalOpen = true;
  boardDeleteModalPromise = new Promise((resolve) => {
    const onClose = (result: boolean) => {
      try { root.unmount(); } catch { }
      container.remove();
      boardDeleteModalOpen = false;
      boardDeleteModalPromise = null;
      resolve(result);
    };
    const Modal = () => (
      React.createElement("div", { className: "fixed inset-0 flex items-center justify-center" },
        React.createElement("div", { className: "absolute inset-0 bg-black/40" }),
        React.createElement("div", { className: "relative z-10 bg-modal rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-[360px] max-w-[90vw] p-5" },
          React.createElement("div", { className: "text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100" }, "Delete board"),
          React.createElement("div", { className: "text-sm text-gray-600 dark:text-gray-300 mb-3" }, message),
          warning
            ? React.createElement(
                "div",
                {
                  className:
                    "flex items-start gap-2 rounded-md bg-amber-500/15 px-3 py-2.5 mb-4 text-sm text-amber-600 dark:text-amber-400",
                },
                React.createElement(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0" }),
                React.createElement("span", null, warning),
              )
            : null,
          React.createElement("div", { className: "flex gap-2 justify-end" },
            React.createElement("button", { className: "px-3 py-1.5 text-sm rounded-md modal-action-hover  ", onClick: () => onClose(false) }, "Cancel"),
            React.createElement("button", { className: "px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700", onClick: () => onClose(true) }, "Delete")
          )
        )
      )
    );
    const root = createRoot(container);
    root.render(React.createElement(Modal));
  });
  return boardDeleteModalPromise;
}

export const boardDeletionGuardExtension = Extension.create({
  name: "boardDeletionGuard",

  addKeyboardShortcuts() {
    return {
      Backspace: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        // Leave a page block intact and skip the caret past it, so continued Backspace
        // keeps wiping the blocks ABOVE the page instead of removing it.
        if (skipPageBlock(editor, "before")) return true;
        const getBoardBeforeCaret = () => {
          const { $from } = selection as any;
          if ($from.parentOffset === 0 && $from.pos > 0) {
            const $prev = state.doc.resolve($from.pos - 1);
            const maybe = ($prev as any).nodeBefore;
            if (maybe && maybe.type?.name === "view_collection") {
              // The board ends at the block boundary ($from.pos - 1); $from.pos sits
              // INSIDE the next block, so the old range deleted that block's opening and
              // left the board. Anchor `to` to the boundary so [from, to] is the board.
              const to = $from.pos - 1;
              const from = to - maybe.nodeSize;
              return { from, to, viewId: maybe.attrs?.blockId ?? null } as { from: number; to: number; viewId: string | null };
            }
          }
          const nodeBefore = ($from as any).nodeBefore;
          if (nodeBefore && nodeBefore.type?.name === "view_collection") {
            return { from: $from.pos - nodeBefore.nodeSize, to: $from.pos, viewId: nodeBefore.attrs?.blockId ?? null } as { from: number; to: number; viewId: string | null };
          }
          return null;
        };

        if ((selection as any).node) {
          const node = (selection as any).node;
          if (node.type.name === "view_collection") {
            const boardId = node.attrs.blockId;
            showBoardDeleteModal("Are you sure you want to delete this board?").then(async (ok) => {
              if (!ok) return;
              deleteRangeWithConfirmMeta(editor, selection.from, selection.to);
              if (boardId) {
                try {
                  await deleteBoardWithOptimisticUpdate(boardId);
                } catch { }
              }
            });
            return true;
          }
        }

        if (!selection.empty) {
          const boards = collectBoardsInRange(state, selection.from, selection.to);
          const removedBoards = getRemovedBoardsAfterDelete(state, selection.from, selection.to);
          const targetBoards = removedBoards.length > 0 ? removedBoards : boards;

          if (targetBoards.length > 0) {
            showBoardDeleteModal(
              getBoardDeleteMessage(targetBoards.length),
              getBoardDeleteWarning(targetBoards.length),
            ).then(async (ok) => {
              if (!ok) return;
              deleteRangeWithConfirmMeta(editor, selection.from, selection.to);
              for (const b of targetBoards) {
                if (!b.viewId) continue;
                try {
                  await deleteBoardWithOptimisticUpdate(b.viewId);
                } catch { }
              }
            });
            return true;
          }
        }

        if (selection.empty) {
          const info = getBoardBeforeCaret();
          if (info) {
            showBoardDeleteModal("Are you sure you want to delete this board?").then(async (ok) => {
              if (!ok) return;
              deleteRangeWithConfirmMeta(editor, info.from, info.to);
              if (info.viewId) {
                try {
                  await deleteBoardWithOptimisticUpdate(info.viewId);
                } catch { }
              }
            });
            return true;
          }
        }

        return false;
      },

      Delete: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        // Leave a page block intact and skip the caret past it, so continued Delete keeps
        // wiping the blocks BELOW the page instead of removing it.
        if (skipPageBlock(editor, "after")) return true;
        const getBoardAfterCaret = () => {
          const { $from } = selection as any;
          const atEnd = $from.parentOffset === $from.parent.content.size;
          if (atEnd) {
            const $next = state.doc.resolve(Math.min(state.doc.nodeSize - 2, $from.pos));
            const maybe = ($next as any).nodeAfter;
            if (maybe && maybe.type?.name === "view_collection") {
              const from = $from.pos;
              const to = from + maybe.nodeSize;
              return { from, to, viewId: maybe.attrs?.blockId ?? null } as { from: number; to: number; viewId: string | null };
            }
          }
          const nodeAfter = ($from as any).nodeAfter;
          if (nodeAfter && nodeAfter.type?.name === "view_collection") {
            return { from: $from.pos, to: $from.pos + nodeAfter.nodeSize, viewId: nodeAfter.attrs?.blockId ?? null } as { from: number; to: number; viewId: string | null };
          }
          return null;
        };

        if ((selection as any).node) {
          const node = (selection as any).node;
          if (node.type.name === "view_collection") {
            const boardId = node.attrs.blockId;
            showBoardDeleteModal("Are you sure you want to delete this board?").then(async (ok) => {
              if (!ok) return;
              deleteRangeWithConfirmMeta(editor, selection.from, selection.to);
              if (boardId) {
                try {
                  await deleteBoardWithOptimisticUpdate(boardId);
                } catch { }
              }
            });
            return true;
          }
        }

        if (!selection.empty) {
          const boards = collectBoardsInRange(state, selection.from, selection.to);
          const removedBoards = getRemovedBoardsAfterDelete(state, selection.from, selection.to);
          const targetBoards = removedBoards.length > 0 ? removedBoards : boards;

          if (targetBoards.length > 0) {
            showBoardDeleteModal(
              getBoardDeleteMessage(targetBoards.length),
              getBoardDeleteWarning(targetBoards.length),
            ).then(async (ok) => {
              if (!ok) return;
              deleteRangeWithConfirmMeta(editor, selection.from, selection.to);
              for (const b of targetBoards) {
                if (!b.viewId) continue;
                try {
                  await deleteBoardWithOptimisticUpdate(b.viewId);
                } catch { }
              }
            });
            return true;
          }
        }

        if (selection.empty) {
          const info = getBoardAfterCaret();
          if (info) {
            showBoardDeleteModal("Are you sure you want to delete this board?").then(async (ok) => {
              if (!ok) return;
              deleteRangeWithConfirmMeta(editor, info.from, info.to);
              if (info.viewId) {
                try {
                  await deleteBoardWithOptimisticUpdate(info.viewId);
                } catch { }
              }
            });
            return true;
          }
        }

        return false;
      },
    };
  },

  addProseMirrorPlugins() {
    // Per-editor handle to this instance's view, set in view() below. Used by
    // filterTransaction to tell whether THIS client's user initiated a delete
    // (its editor has focus) vs. a change arriving from elsewhere.
    let pluginView: any = null;
    return [
      new Plugin({
        key: new PluginKey('boardDeletionGlobalGuard'),
        props: {
          // Strip page/board/meeting nodes out of PASTED content — these blocks are
          // never created from the editor, and pasting one yields a node with no
          // backing datasource/view/children → render crash.
          //
          // We use handlePaste (paste-only), NOT transformPasted, because ProseMirror
          // runs transformPasted on drag-and-drop drops too — which would strip a
          // board/page/meeting being MOVED within the editor (its data still exists, so
          // a move must be allowed). handlePaste fires only on real paste events.
          handlePaste(view, event, slice) {
            // ── Large-paste guard (Notion-style "import instead of paste") ──────
            // Very large pastes create one synced block per node → editor jank.
            // Measure the RAW clipboard string (cheap, already in memory) BEFORE
            // ProseMirror applies the slice, and above the threshold cancel the
            // inline paste and hand off to the import flow via a window event.
            const html = event?.clipboardData?.getData("text/html") ?? "";
            const text = event?.clipboardData?.getData("text/plain") ?? "";
            const size = Math.max(html.length, text.length);
            if (size > MAX_PASTE_CONTENT_CHARS) {
              // Block the inline paste and show the "too large — import instead"
              // notice. The user imports manually via the normal flow.
              event?.preventDefault?.();
              window.dispatchEvent(new CustomEvent(LARGE_PASTE_EVENT));
              return true; // handled — inline paste cancelled
            }

            const newContent = stripNonEditorCreatableNodes(slice.content);
            if (newContent.eq(slice.content)) return false; // nothing to strip → default paste
            // view_collection/page/meeting are atom nodes, so the slice's open depths can
            // never point inside one — removing them leaves openStart/openEnd valid.
            const cleaned =
              newContent.childCount === 0
                ? Slice.empty
                : new Slice(newContent, slice.openStart, slice.openEnd);
            view.dispatch(
              view.state.tr
                .replaceSelection(cleaned)
                .scrollIntoView()
                .setMeta("paste", true)
                .setMeta("uiEvent", "paste"),
            );
            return true; // we handled this paste
          },
        },
        // Undo/redo can re-introduce a node whose backing data is gone, which crashes on
        // mount. This runs synchronously inside the same dispatch, so a restored node is
        // removed BEFORE its NodeView mounts. We strip:
        //   • page / meeting — never created from the editor, so any restored one is bad.
        //   • view_collection (board) — ONLY if its id is in deletedBoardIds (it was
        //     deleted). A board that was merely created-then-undone is NOT in that set,
        //     so redo-of-board-create still restores it normally.
        appendTransaction(transactions, oldState, newState) {
          const isUndoRedo = transactions.some(
            (t: any) =>
              t.getMeta("history$") ||
              t.getMeta("uiEvent") === "undo" ||
              t.getMeta("uiEvent") === "redo",
          );
          if (!isUndoRedo) return null;

          const GUARD_TYPES = ["page", "meeting", "button"];

          // blockIds of guarded nodes that existed BEFORE this undo/redo.
          const oldIds = new Set<string>();
          oldState.doc.descendants((node: any) => {
            if (GUARD_TYPES.includes(node.type?.name) && node.attrs?.blockId) {
              oldIds.add(node.attrs.blockId);
            }
            return true;
          });

          // A guarded node present now but absent before = one the undo/redo restored.
          // (A move keeps the same blockId in both states, so it is correctly kept.)
          const toRemove: Array<{ pos: number; size: number }> = [];
          newState.doc.descendants((node: any, pos: number) => {
            const typeName = node.type?.name;
            if (GUARD_TYPES.includes(typeName)) {
              const id = node.attrs?.blockId;
              if (!id || !oldIds.has(id)) {
                toRemove.push({ pos, size: node.nodeSize });
                return false; // don't descend into a node we're removing
              }
            } else if (typeName === "view_collection") {
              // A board is fine to keep UNLESS it was deleted — then undo is trying to
              // restore a board whose datasource is gone, so strip it.
              const id = node.attrs?.blockId;
              if (id && deletedBoardIds.has(id)) {
                toRemove.push({ pos, size: node.nodeSize });
                return false;
              }
            }
            return true;
          });

          if (toRemove.length === 0) return null;
          let tr = newState.tr;
          toRemove.sort((a, b) => b.pos - a.pos); // delete back-to-front so positions stay valid
          for (const { pos, size } of toRemove) tr = tr.delete(pos, pos + size);

          // Edge: if a guarded node was the document's ONLY block, delete() no-ops on a
          // "block+" schema and the node survives → it would still crash. Replace any
          // survivor with an empty paragraph so the bad atom can never remain in the doc.
          const paragraph = newState.schema.nodes.paragraph;
          if (paragraph) {
            const survivors: Array<{ pos: number; size: number }> = [];
            tr.doc.descendants((node: any, pos: number) => {
              const typeName = node.type?.name;
              if (GUARD_TYPES.includes(typeName)) {
                const id = node.attrs?.blockId;
                if (!id || !oldIds.has(id)) {
                  survivors.push({ pos, size: node.nodeSize });
                  return false;
                }
              } else if (typeName === "view_collection") {
                const id = node.attrs?.blockId;
                if (id && deletedBoardIds.has(id)) {
                  survivors.push({ pos, size: node.nodeSize });
                  return false;
                }
              }
              return true;
            });
            survivors.sort((a, b) => b.pos - a.pos);
            for (const { pos, size } of survivors) {
              tr = tr.replaceWith(pos, pos + size, paragraph.create());
            }
          }

          tr = tr.setMeta("addToHistory", false);
          // Silent visual correction: the block was already deleted in the backend when
          // the user first deleted it. Mark `fromParser` so the block tracker skips it
          // (extensions.ts:386) — otherwise it reads this removal as a brand-new deletion
          // and fires a redundant DELETE op + a spurious parent reorder.
          tr = tr.setMeta("fromParser", true);
          return tr;
        },
        filterTransaction: (tr, state) => {
          if (tr.getMeta(CONFIRM_META)) return true;

          // Remote collaborative edits: a board deleted by ANOTHER user arrives
          // here through y-prosemirror's ySyncPlugin. That user already
          // confirmed the deletion on their side, and ProseMirror transaction
          // metas (CONFIRM_META / fromParser) do NOT travel over Yjs — only the
          // document delta does. So without this exemption the synced deletion
          // looks like a brand-new local delete: the guard would (a) re-open the
          // "Delete board?" modal on this client and (b) REJECT the transaction,
          // which desyncs the ProseMirror doc from the Y.Doc. The result is the
          // modal bouncing between the two browsers and neither ever committing
          // the delete. Apply remote board removals silently.
          if ((tr.getMeta(ySyncPluginKey) as any)?.isChangeOrigin === true) return true;

          if (!tr.docChanged) return true;

          // Ignore programmatic re-parser / setContentSilently swaps. These
          // transactions are state-syncs from the block re-parser or initial
          // hydration (see advanced-editor.tsx and setContentSilently.ts) —
          // not user delete actions. They already tag themselves with
          // `fromParser: true` and BlockIdAndChangeTrackerExtension honors it
          // (extensions.ts:343). Without this exemption, the before/after diff
          // below misclassifies reconstruction-driven board node moves as
          // "user deleted a board" and pops the "Delete board?" modal during
          // slash-command create, reload, title change, and task creation.
          if (tr.getMeta('fromParser') === true) return true;

          const collectBoards = (doc: any) => {
            const list: Array<{ from: number; to: number; viewId: string | null }> = [];
            doc.descendants((node: any, pos: number) => {
              if (node.type?.name === 'view_collection') {
                list.push({ from: pos, to: pos + node.nodeSize, viewId: node.attrs?.blockId ?? null });
              }
            });
            return list;
          };

          const beforeBoards = collectBoards(state.doc);
          const afterBoards = collectBoards(tr.doc);
          const afterIds = new Set(afterBoards.map(b => b.viewId || `${b.from}:${b.to}`));
          const removed = beforeBoards.filter(b => !afterIds.has(b.viewId || `${b.from}:${b.to}`));

          if (removed.length === 0) return true;

          // Defense-in-depth for collaboration: a board vanished, but only the
          // client whose USER actually performed the deletion should confirm
          // it. The initiating editor has focus; a client merely receiving the
          // change (remote sync, or a background/programmatic edit) does not.
          // If this editor isn't focused, apply the removal silently — never
          // pop the dialog on the non-initiating client. This backstops the
          // isChangeOrigin check above in case a remote apply ever reaches here
          // without that meta.
          if (
            tr.getMeta(USER_BOARD_DELETE_META) !== true &&
            pluginView &&
            typeof pluginView.hasFocus === "function" &&
            !pluginView.hasFocus()
          ) {
            return true;
          }

          setTimeout(() => {
            const message = getBoardDeleteMessage(removed.length);
            const warning = getBoardDeleteWarning(removed.length);
            showBoardDeleteModal(message, warning).then(async (ok) => {
              if (!ok) return;
              // This plugin's own view — every editor registers the guard and
              // overwrites the global, so it can point at a different editor.
              const viewRef = pluginView ?? (window as any)?.__tiptapView;
              if (!viewRef) return;
              const curState = viewRef.state;

              const targets: Array<{ from: number; to: number; viewId: string | null }> = [];
              const targetIds = new Set(removed.map(r => r.viewId).filter(Boolean) as string[]);
              curState.doc.descendants((node: any, pos: number) => {
                if (node.type?.name === 'view_collection') {
                  const vid = node.attrs?.blockId ?? null;
                  if ((vid && targetIds.has(vid)) || (!vid && removed.some(r => !r.viewId))) {
                    targets.push({ from: pos, to: pos + node.nodeSize, viewId: vid });
                  }
                }
              });

              // The backend delete is driven by the doc mutation below, so
              // there is nothing to do if the node isn't here.
              if (targets.length === 0) return;

              targets.sort((a, b) => b.from - a.from);
              let trDel = curState.tr;
              for (const t of targets) {
                trDel = trDel.delete(t.from, t.to);
              }
              if (trDel.docChanged) {
                trDel = trDel.setMeta(CONFIRM_META, true);
                viewRef.dispatch(trDel);
              }

              for (const t of targets) {
                if (!t.viewId) continue;
                try {
                  await deleteBoardWithOptimisticUpdate(t.viewId);
                } catch { }
              }
            });
          }, 0);

          return false;
        },
        view(view) {
          pluginView = view;
          (window as any).__tiptapView = view;
          return {
            update(v: any) { pluginView = v; },
            destroy() { pluginView = null; },
          };
        },
      }),
    ];
  },
});

export default boardDeletionGuardExtension;


